import { useMemo } from 'react';
import { FormSchemaConfig } from '../../types/form/schema.types';
import { FormState } from '../../types/core/form-state.types';
import { DisabledStateManager } from '../types';
import {
  validateCheckbox,
  validateRequired,
  validateAmount,
  validateTagPicker,
} from '../Utils';

/**
 * Dubai Transfer Requisition Payment Details Schema
 *
 * This schema handles the Dubai Transfer Requisition form with three main sections:
 * - Debit: For disbursement, nominal, or client transactions
 * - Credit: For corresponding credit entries
 * - Approvers: For partner and fee earner approvals
 *
 * The schema uses a centralized disabled state management system to ensure
 * consistent behavior across all form fields.
 */

// Interface for Dubai Transfer Requisition form data
export interface DubaiTransferRequisitionData {
  // Debit section
  debitDisbursement?: boolean;
  debitNominal?: boolean;
  debitClient?: boolean;
  debitClientName?: string[];
  debitMatterName?: string[];
  debitClientMatterNumber?: string;
  debitDate?: string;
  debitAmount?: number;
  debitDescription?: string;

  // Credit section
  creditDisbursement?: boolean;
  creditNominal?: boolean;
  creditClient?: boolean;
  creditClientName?: string[];
  creditMatterName?: string[];
  creditClientMatterNumber?: string;
  debitBillNumber?: string;
  creditBillNumber?: string;

  // Approvers section
  firstPartner?: string[];
  secondPartner?: string[];
  feeEarner?: string[];
}

/**
 * useDubaiTransferRequisitionPaymentDetailsSchema
 *
 * A React hook that returns a dynamic form schema object (FormSchemaConfig) for handling payment details.
 * It sets up columns for a table, defines sections and fields in a form, and conditionally shows/hides
 * certain fields using the "modern" dependency format (conditions array + logicalOperator).
 *
 * The returned value from this hook is a FormSchemaConfig object that can be used by a form builder or
 * renderer to display and manage the described fields, sections, and dependencies.
 *
 * @param formState - The current form state including user data and completed sections
 * @returns A FormSchemaConfig object describing the entire "Payment Request Form" structure
 */
export const useDubaiTransferRequisitionPaymentDetailsSchema = (
  formState: FormState
): FormSchemaConfig => {
  // ============= Centralized Disabled State Management =============
  const isEditAllowed = Array.isArray(formState.data?.userAssignedSections)
    ? (formState.data?.userAssignedSections as string[]).includes(
        'dubai_transfer_requisition_payment_details'
      )
    : false;

  const disabledState = useMemo<DisabledStateManager>(() => {
    const isFormCompleted =
      formState.completedSections?.includes(
        'dubai_transfer_requisition_payment_details'
      ) || false;
    const isFormRejected = formState.isRejected || false;

    return {
      isFormCompleted,
      isFieldDisabled: (
        fieldType: 'readonly' | 'editable' | 'conditional' = 'editable'
      ) => {
        switch (fieldType) {
          case 'readonly':
            return true;
          case 'conditional':
            return (isFormCompleted && !isFormRejected) || !isEditAllowed;
          case 'editable':
          default:
            return (isFormCompleted && !isFormRejected) || !isEditAllowed;
        }
      },

      isTableDisabled: () =>
        (isFormCompleted && !isFormRejected) || !isEditAllowed,
      isPaymentTotalDisabled: () =>
        (isFormCompleted && !isFormRejected) || !isEditAllowed,
      isTagPickerDisabled: () =>
        (isFormCompleted && !isFormRejected) || !isEditAllowed,
      isFileUploadDisabled: () =>
        (isFormCompleted && !isFormRejected) || !isEditAllowed,
      isTextAreaDisabled: () =>
        (isFormCompleted && !isFormRejected) || !isEditAllowed,
      isRadioDisabled: () =>
        (isFormCompleted && !isFormRejected) || !isEditAllowed,
      isSelectDisabled: () =>
        (isFormCompleted && !isFormRejected) || !isEditAllowed,
    };
  }, [formState.completedSections, isEditAllowed, formState.isRejected]);

  const formSchema = useMemo<FormSchemaConfig>(
    () => ({
      sections: [
        {
          isGrouped: true,
          isGroupedAccordion: true,
          sections: [
            {
              title: { text: 'Debit' },
              layout: 'auto',
              fields: [
                {
                  type: 'checkbox',
                  name: 'debitType',
                  label: 'Debit Type',
                  required: true,
                  validate: (value) => validateCheckbox(value, 'Debit Type'),
                  options: [
                    {
                      value: 'disbursement',
                      label: 'Disbursement',
                      disabled: false,
                    },
                    {
                      value: 'nominal',
                      label: 'Nominal',
                      disabled: false,
                    },
                    {
                      value: 'client',
                      label: 'Client',
                      disabled: false,
                    },
                  ],
                  disabled: disabledState.isFieldDisabled('conditional'),
                },
                {
                  type: 'text',
                  name: 'debitGeneralLedgerCodes',
                  label: 'General Ledger Codes',
                  required: true,
                  validate: (value) =>
                    validateRequired(value, 'General Ledger Codes'),
                  disabled: disabledState.isFieldDisabled('conditional'),
                  dependencies: [
                    {
                      type: 'show',
                      logicalOperator: 'AND',
                      conditions: [
                        {
                          field: 'debitType',
                          operator: 'equals',
                          value: 'nominal',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'clientMatter',
                  name: 'debitClientMatter',
                  label: 'Client and Matter',
                  required: true,
                  disabled: disabledState.isFieldDisabled('conditional'),
                  dependencies: [
                    {
                      type: 'show',
                      logicalOperator: 'OR',
                      conditions: [
                        {
                          field: 'debitType',
                          operator: 'equals',
                          value: 'client',
                        },
                        {
                          field: 'debitType',
                          operator: 'equals',
                          value: 'disbursement',
                        },
                      ],
                    },
                  ],
                  extras: {
                    fullWidth: true,
                    clientMatter: {
                      clientNameField: 'debitClientName',
                      matterNameField: 'debitMatterName',
                      clientUnoField: 'debitClientUno',
                      matterUnoField: 'debitMatterUno',
                      clientMatterNumberField: 'debitClientMatterNumber',
                      selectedClientLabelField: 'debitSelectedClientLabel',
                      selectedMatterLabelField: 'debitSelectedMatterLabel',
                      office: formState.office,
                      clientLabel: 'Client',
                      matterLabel: 'Matter',
                    },
                  },
                },
                {
                  type: 'text',
                  name: 'debitSelectedClientLabel',
                  label: 'Client Name',
                  placeholder: 'Auto-populated based on selected client',
                  required: true,
                  validate: (value) =>
                    !value || (Array.isArray(value) && value.length === 0)
                      ? 'Client Name is required'
                      : undefined,
                  value: formState.data?.debitSelectedClientLabel,
                  disabled: disabledState.isFieldDisabled('readonly'),
                  dependencies: [
                    {
                      type: 'show',
                      logicalOperator: 'OR',
                      conditions: [
                        {
                          field: 'debitType',
                          operator: 'equals',
                          value: 'client',
                        },
                        {
                          field: 'debitType',
                          operator: 'equals',
                          value: 'disbursement',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'text',
                  name: 'debitSelectedMatterLabel',
                  label: 'Matter Name',
                  placeholder: 'Auto-populated based on selected matter',
                  required: true,
                  validate: (value) =>
                    !value || (Array.isArray(value) && value.length === 0)
                      ? 'Matter Name is required'
                      : undefined,
                  value: formState.data?.debitSelectedMatterLabel,
                  disabled: disabledState.isFieldDisabled('readonly'),
                  dependencies: [
                    {
                      type: 'show',
                      logicalOperator: 'OR',
                      conditions: [
                        {
                          field: 'debitType',
                          operator: 'equals',
                          value: 'client',
                        },
                        {
                          field: 'debitType',
                          operator: 'equals',
                          value: 'disbursement',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'text',
                  name: 'debitClientMatterNumber',
                  label: 'Client and Matter Number',
                  disabled: disabledState.isFieldDisabled('readonly'),
                  placeholder:
                    'Auto-populated based on selected client and matter',
                  value: formState.data?.debitClientMatterNumber,
                  dependencies: [
                    {
                      type: 'show',
                      logicalOperator: 'OR',
                      conditions: [
                        {
                          field: 'debitType',
                          operator: 'equals',
                          value: 'client',
                        },
                        {
                          field: 'debitType',
                          operator: 'equals',
                          value: 'disbursement',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'checkbox',
                  name: 'debitShowBillNumber',
                  label: 'Bill required?',
                  options: [
                    {
                      value: 'yes',
                      label: 'Yes',
                      disabled: false,
                    },
                    {
                      value: 'no',
                      label: 'No',
                      disabled: false,
                    },
                  ],
                  disabled: disabledState.isFieldDisabled('conditional'),
                },
                {
                  type: 'text',
                  name: 'debitBillNumber',
                  label: 'Bill Number',
                  required: formState.data?.debitShowBillNumber === 'yes',
                  validate: (value) => validateRequired(value, 'Bill Number'),
                  disabled: disabledState.isFieldDisabled('conditional'),
                  dependencies: [
                    {
                      type: 'show',
                      logicalOperator: 'AND',
                      conditions: [
                        {
                          field: 'debitShowBillNumber',
                          operator: 'equals',
                          value: 'yes',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'date',
                  name: 'debitDate',
                  label: 'Date',
                  required: true,
                  validate: (value) => validateRequired(value, 'Date'),
                  disabled: disabledState.isFieldDisabled('conditional'),
                },
                {
                  type: 'number',
                  name: 'debitAmount',
                  label: 'Amount',
                  required: true,
                  validate: validateAmount,
                  disabled: disabledState.isFieldDisabled('conditional'),
                  placeholder: 'Enter amount',
                  extras: {
                    textInput: {
                      formatCurrency: true,
                    },
                  },
                },
                {
                  type: 'textArea',
                  name: 'debitDescription',
                  label: 'Description',
                  disabled: disabledState.isTextAreaDisabled(),
                  extras: {
                    textArea: {
                      maxLength: 500,
                      showCharCount: true,
                      rows: 4,
                      autoResize: true,
                    },
                  },
                },
              ],
              extras: {
                grouped: true,
                evenSpacing: true,
                defaultExpanded: true,
              },
            },
            {
              title: { text: 'Credit' },
              layout: 'auto',
              fields: [
                {
                  type: 'checkbox',
                  name: 'creditType',
                  label: 'Credit Type',
                  required: true,
                  validate: (value) => validateCheckbox(value, 'Credit Type'),
                  options: [
                    {
                      value: 'disbursement',
                      label: 'Disbursement',
                      disabled: false,
                    },
                    {
                      value: 'nominal',
                      label: 'Nominal',
                      disabled: false,
                    },
                    {
                      value: 'client',
                      label: 'Client',
                      disabled: false,
                    },
                  ],
                  disabled: disabledState.isFieldDisabled('conditional'),
                },
                {
                  type: 'text',
                  name: 'creditGeneralLedgerCodes',
                  label: 'General Ledger Codes',
                  required: true,
                  validate: (value) =>
                    validateRequired(value, 'General Ledger Codes'),
                  disabled: disabledState.isFieldDisabled('conditional'),
                  dependencies: [
                    {
                      type: 'show',
                      logicalOperator: 'AND',
                      conditions: [
                        {
                          field: 'creditType',
                          operator: 'equals',
                          value: 'nominal',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'clientMatter',
                  name: 'creditClientMatter',
                  label: 'Client and Matter',
                  required: true,
                  disabled: disabledState.isFieldDisabled('conditional'),
                  dependencies: [
                    {
                      type: 'show',
                      logicalOperator: 'OR',
                      conditions: [
                        {
                          field: 'creditType',
                          operator: 'equals',
                          value: 'client',
                        },
                        {
                          field: 'creditType',
                          operator: 'equals',
                          value: 'disbursement',
                        },
                      ],
                    },
                  ],
                  extras: {
                    fullWidth: true,
                    clientMatter: {
                      clientNameField: 'creditClientName',
                      matterNameField: 'creditMatterName',
                      clientUnoField: 'creditClientUno',
                      matterUnoField: 'creditMatterUno',
                      clientMatterNumberField: 'creditClientMatterNumber',
                      office: formState.office,
                      clientLabel: 'Client',
                      matterLabel: 'Matter',
                    },
                  },
                },
                {
                  type: 'text',
                  name: 'creditClientName',
                  label: 'Client Name',
                  placeholder: 'Auto-populated based on selected client.',
                  required: true,
                  validate: (value) =>
                    !value || (Array.isArray(value) && value.length === 0)
                      ? 'Client Name is required'
                      : undefined,
                  value: formState.data?.creditClientName,
                  disabled: disabledState.isFieldDisabled('readonly'),
                  dependencies: [
                    {
                      type: 'show',
                      logicalOperator: 'OR',
                      conditions: [
                        {
                          field: 'creditType',
                          operator: 'equals',
                          value: 'client',
                        },
                        {
                          field: 'creditType',
                          operator: 'equals',
                          value: 'disbursement',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'text',
                  name: 'creditMatterName',
                  label: 'Matter Name',
                  placeholder: 'Auto-populated based on selected matter.',
                  required: true,
                  validate: (value) =>
                    !value || (Array.isArray(value) && value.length === 0)
                      ? 'Matter Name is required'
                      : undefined,
                  value: formState.data?.creditMatterName,
                  disabled: disabledState.isFieldDisabled('readonly'),
                  dependencies: [
                    {
                      type: 'show',
                      logicalOperator: 'OR',
                      conditions: [
                        {
                          field: 'creditType',
                          operator: 'equals',
                          value: 'client',
                        },
                        {
                          field: 'creditType',
                          operator: 'equals',
                          value: 'disbursement',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'text',
                  name: 'creditClientMatterNumber',
                  label: 'Client and Matter Number',
                  disabled: disabledState.isFieldDisabled('readonly'),
                  placeholder:
                    'Auto-populated based on selected client and matter',
                  value: formState.data?.creditClientMatterNumber,
                  dependencies: [
                    {
                      type: 'show',
                      logicalOperator: 'OR',
                      conditions: [
                        {
                          field: 'creditType',
                          operator: 'equals',
                          value: 'client',
                        },
                        {
                          field: 'creditType',
                          operator: 'equals',
                          value: 'disbursement',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'checkbox',
                  name: 'creditShowBillNumber',
                  label: 'Bill required?',
                  options: [
                    {
                      value: 'yes',
                      label: 'Yes',
                      disabled: false,
                    },
                    {
                      value: 'no',
                      label: 'No',
                      disabled: false,
                    },
                  ],
                  disabled: disabledState.isFieldDisabled('conditional'),
                },
                {
                  type: 'text',
                  name: 'creditBillNumber',
                  label: 'Bill Number',
                  required: formState.data?.creditShowBillNumber === 'yes',
                  validate: (value) => validateRequired(value, 'Bill Number'),
                  disabled: disabledState.isFieldDisabled('conditional'),
                  dependencies: [
                    {
                      type: 'show',
                      logicalOperator: 'AND',
                      conditions: [
                        {
                          field: 'creditShowBillNumber',
                          operator: 'equals',
                          value: 'yes',
                        },
                      ],
                    },
                  ],
                },
              ],
              extras: {
                grouped: true,
                evenSpacing: true,
                defaultExpanded: true,
              },
            },
            {
              title: { text: 'Approvers' },
              isGrouped: true,
              isGroupedAccordion: true,
              sections: [
                {
                  title: {
                    text: 'Fee Earner (Informational not an actual approver)',
                  },
                  layout: 'auto',
                  fields: [
                    {
                      type: 'tagPicker',
                      name: 'feeEarner',
                      label: 'Fee Earner',
                      required: true,
                      validate: (value) =>
                        validateTagPicker(value, 'Fee Earner'),
                      disabled: disabledState.isTagPickerDisabled(),
                      extras: {
                        tagPicker: {
                          minSearchLength: 4,
                          maxTags: 1,
                          createNewTags: false,
                        },
                        fullWidth: true,
                      },
                    },
                  ],
                  extras: {
                    grouped: true,
                    evenSpacing: true,
                    defaultExpanded: true,
                  },
                },
                {
                  title: { text: 'Partners' },
                  layout: 'auto',
                  fields: [
                    {
                      type: 'tagPicker',
                      name: 'firstPartner',
                      label: 'First Partner',
                      required: true,
                      validate: (value) =>
                        validateTagPicker(value, 'First Partner'),
                      disabled: disabledState.isTagPickerDisabled(),
                      extras: {
                        tagPicker: {
                          minSearchLength: 4,
                          maxTags: 1,
                          createNewTags: false,
                        },
                      },
                    },
                    {
                      type: 'tagPicker',
                      name: 'secondPartner',
                      label: 'Second Partner',
                      required: true,
                      validate: (value) =>
                        validateTagPicker(value, 'Second Partner'),
                      disabled: disabledState.isTagPickerDisabled(),
                      dependencies: [
                        {
                          type: 'show',
                          logicalOperator: 'AND',
                          conditions: [
                            {
                              field: 'debitType',
                              operator: 'equals',
                              value: 'client',
                            },
                            {
                              field: 'creditType',
                              operator: 'equals',
                              value: 'client',
                            },
                          ],
                        },
                      ],
                      extras: {
                        tagPicker: {
                          minSearchLength: 4,
                          maxTags: 1,
                          createNewTags: false,
                        },
                      },
                    },
                  ],
                  extras: {
                    grouped: true,
                    evenSpacing: true,
                    defaultExpanded: true,
                  },
                },
              ],
              extras: {
                grouped: true,
                evenSpacing: true,
                defaultExpanded: true,
              },
            },
          ],
        },
      ],
      calculations: {},
    }),
    [disabledState, formState.office, formState.data],
  );

  // The returned object is the complete form schema, ready for rendering
  return formSchema;
};
